// Thin per-module hooks layer over TanStack Query + the typed fetch wrapper
// (src/api/client.ts). Module-specific hooks (e.g. hooks/useSiteStatus.ts, and every
// later web module goal G20-G26) should be built from these two primitives rather
// than calling `useQuery`/`useMutation` directly, so query-key shape and error
// handling stay consistent across the app.
import { useMutation, useQuery, type UseMutationOptions, type UseQueryOptions } from '@tanstack/react-query';
import { apiFetch, type ApiFetchOptions, type HttpMethod } from '../api/client';

export type ApiQueryKey = readonly unknown[];

/** GET-style query. `path` may include TanStack Query's usual key-shaped params by
 * folding them into `key` (for cache identity) and into `query`/the path itself
 * (for the actual request). */
export function useApiQuery<T>(
  key: ApiQueryKey,
  path: string,
  options: {
    query?: ApiFetchOptions['query'];
    enabled?: boolean;
  } & Omit<UseQueryOptions<T, Error, T, ApiQueryKey>, 'queryKey' | 'queryFn'> = {},
) {
  const { query, enabled, ...rest } = options;
  return useQuery<T, Error, T, ApiQueryKey>({
    queryKey: key,
    queryFn: ({ signal }) => apiFetch<T>(path, { query, signal }),
    enabled,
    ...rest,
  });
}

/** POST/PUT/PATCH/DELETE-style mutation against a fixed `path`. Posting endpoints
 * (plan.md S3) are dedicated per-action routes, so `path` is normally a literal
 * (e.g. `/purchasing/purchase-orders/${id}/post`) built by the calling module hook. */
export function useApiMutation<TResponse, TBody = void>(
  path: string,
  method: HttpMethod = 'POST',
  options: Omit<UseMutationOptions<TResponse, Error, TBody>, 'mutationFn'> = {},
) {
  return useMutation<TResponse, Error, TBody>({
    mutationFn: (body: TBody) => apiFetch<TResponse>(path, { method, body }),
    ...options,
  });
}
